import React from 'react';
import {
    Alert,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { submitIssueReport } from '../../api';
import { logError, logInfo, subscribeToLogs } from '../../logging/loggingService';

const MAX_LOGS = 10;
const MAX_LOGS_SENT = 5;

export default function IssueReportDialog({ open, onClose, reporter }) {
    const location = useLocation();
    const [title, setTitle] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [steps, setSteps] = React.useState('');
    const [expectedResult, setExpectedResult] = React.useState('');
    const [actualResult, setActualResult] = React.useState('');
    const [contactEmail, setContactEmail] = React.useState('');
    const [contactName, setContactName] = React.useState('');
    const [includeLogs, setIncludeLogs] = React.useState(true);
    const [recentLogs, setRecentLogs] = React.useState([]);
    const [feedback, setFeedback] = React.useState(null);
    const [submitting, setSubmitting] = React.useState(false);

    React.useEffect(() => {
        const unsubscribe = subscribeToLogs((entry) => {
            if (!entry || (entry.level !== 'error' && entry.level !== 'warn')) {
                return;
            }
            setRecentLogs((prev) => {
                const next = [entry, ...prev];
                return next.slice(0, MAX_LOGS);
            });
        });
        return () => unsubscribe();
    }, []);

    React.useEffect(() => {
        if (!open) {
            return;
        }
        const defaultName = buildReporterName(reporter);
        setTitle('');
        setDescription('');
        setSteps('');
        setExpectedResult('');
        setActualResult('');
        setContactEmail(reporter?.email || '');
        setContactName(defaultName);
        setIncludeLogs(true);
        setFeedback(null);
        setSubmitting(false);
    }, [open, reporter]);

    const handleClose = () => {
        if (submitting) {
            return;
        }
        onClose?.();
    };

    const hasRequired = title.trim() && description.trim();
    const logsToSend = includeLogs ? recentLogs.slice(0, MAX_LOGS_SENT) : [];

    const handleSubmit = async () => {
        if (!hasRequired || submitting) {
            return;
        }
        setSubmitting(true);
        setFeedback(null);

        const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
        const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
        const locale = typeof navigator !== 'undefined' ? navigator.language : '';
        const referrer = typeof document !== 'undefined' ? document.referrer : '';

        const payload = {
            title: title.trim(),
            description: description.trim(),
            stepsToReproduce: steps.trim() || undefined,
            expectedResult: expectedResult.trim() || undefined,
            actualResult: actualResult.trim() || undefined,
            contactEmail: contactEmail.trim() || undefined,
            contactName: contactName.trim() || undefined,
            pageUrl,
            userAgent,
            locale,
            appVersion: process.env.REACT_APP_VERSION || undefined,
            metadata: {
                path: location?.pathname,
                search: location?.search,
                hash: location?.hash,
                referrer: referrer || undefined,
                userId: reporter?.id || undefined,
            },
            logs: logsToSend,
        };

        try {
            const result = await submitIssueReport(payload);
            const created = Boolean(result?.data?.created);
            const issueUrl = result?.data?.issueUrl;
            const message = result?.data?.message
                || (created
                    ? 'Ticket erstellt. Danke für die Meldung.'
                    : 'Meldung wurde verarbeitet.');
            setFeedback({ severity: 'success', message, issueUrl });
            if (created) {
                setTitle('');
                setDescription('');
                setSteps('');
                setExpectedResult('');
                setActualResult('');
            }
            logInfo('Issue report submitted', { component: 'IssueReportDialog', created, issueUrl });
        } catch (error) {
            logError('Issue report failed', error, { component: 'IssueReportDialog' });
            setFeedback({
                severity: 'error',
                message: 'Meldung konnte nicht gesendet werden. Bitte später erneut versuchen.',
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Fehler melden</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {feedback && (
                        <Alert severity={feedback.severity}>
                            <Stack spacing={0.5}>
                                <Typography variant="body2">{feedback.message}</Typography>
                            </Stack>
                        </Alert>
                    )}
                    <TextField
                        label="Titel"
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        required
                        fullWidth
                        autoFocus
                    />
                    <TextField
                        label="Beschreibung"
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        required
                        fullWidth
                        multiline
                        minRows={3}
                    />
                    <TextField
                        label="Schritte (optional)"
                        value={steps}
                        onChange={(event) => setSteps(event.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                    />
                    <TextField
                        label="Erwartetes Ergebnis (optional)"
                        value={expectedResult}
                        onChange={(event) => setExpectedResult(event.target.value)}
                        fullWidth
                    />
                    <TextField
                        label="Tatsächliches Ergebnis (optional)"
                        value={actualResult}
                        onChange={(event) => setActualResult(event.target.value)}
                        fullWidth
                    />
                    <TextField
                        label="Kontakt (optional)"
                        value={contactName}
                        onChange={(event) => setContactName(event.target.value)}
                        fullWidth
                    />
                    <TextField
                        label="E-Mail (optional)"
                        value={contactEmail}
                        onChange={(event) => setContactEmail(event.target.value)}
                        type="email"
                        fullWidth
                    />
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={includeLogs}
                                onChange={(event) => setIncludeLogs(event.target.checked)}
                            />
                        )}
                        label={`Letzte Logs anhängen (${recentLogs.length})`}
                    />
                    <Typography variant="caption" color="text.secondary">
                        Seite: {location?.pathname || '-'}
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting}>
                    Abbrechen
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!hasRequired || submitting}
                >
                    {submitting ? 'Senden...' : 'Senden'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function buildReporterName(reporter) {
    if (!reporter) {
        return '';
    }
    const parts = [reporter.firstname, reporter.lastname].filter(Boolean);
    if (parts.length) {
        return parts.join(' ');
    }
    return reporter.username || '';
}
